"use client";

import { AlertTriangle } from "lucide-react";
import { useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  getOrCreateCloudWatchRumErrorId,
  recordCloudWatchRumError,
} from "@/lib/client/cloudwatch-rum";

function createClientErrorId() {
  return `client_${Math.random().toString(36).slice(2, 10)}`;
}

function getSafeErrorDigest(value: string | undefined) {
  const digest = value?.trim();
  return digest && /^[A-Za-z0-9_.:-]{1,160}$/.test(digest) ? digest : undefined;
}

export default function ErrorBoundary({
  error,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const errorId = useMemo(() => {
    const requestedErrorId =
      getSafeErrorDigest(error.digest) ?? createClientErrorId();
    return getOrCreateCloudWatchRumErrorId(error, requestedErrorId);
  }, [error, error.digest]);

  useEffect(() => {
    const digest = getSafeErrorDigest(error.digest);
    recordCloudWatchRumError(error, {
      ...(digest ? { digest } : {}),
      errorId,
      handled: true,
      source: "app_error_boundary",
    });
  }, [error, errorId]);

  return (
    <main className="flex min-h-svh items-center justify-center bg-background px-6">
      <div className="flex w-full max-w-sm flex-col items-start gap-4">
        <AlertTriangle className="size-7 text-muted-foreground" />
        <div className="space-y-2">
          <h1 className="text-xl font-semibold text-foreground">
            ページを読み込めませんでした
          </h1>
          <p className="text-sm text-muted-foreground">
            再読み込みするか、前のページに戻ってください。
          </p>
          <p className="text-sm text-muted-foreground">
            問い合わせID:<code>{errorId}</code>
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="button" onClick={() => window.location.reload()}>
            再読み込み
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => window.history.back()}
          >
            前のページへ
          </Button>
        </div>
      </div>
    </main>
  );
}
